import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "./InvoiceForm";

export const EMPTY_PURCHASE = {
  purchase_date: new Date().toISOString().slice(0, 10), status: "Agreed", valuation_id: "", seller_type: "private", seller_name: "",
  seller_address: "", seller_postcode: "", seller_phone: "", seller_email: "", registration: "", vin: "", make: "", model: "",
  derivative: "", colour: "", engine_no: "", first_registration: "", mileage: "", mot_expiry: "", price: "", outstanding_finance: "",
  finance_company: "", buyer_representative: "", notes: "",
};

export const PURCHASE_STATUSES = ["Agreed", "Collected", "Paid", "Cancelled"];

const SECTIONS = [
  ["Purchase", [["purchase_date", "Date of agreement", "date"], ["status", "Status", "select", PURCHASE_STATUSES], ["price", "Agreed purchase price (£)", "number"], ["buyer_representative", "Our representative"]]],
  ["Seller", [["seller_type", "Seller type", "select", [["private", "Private individual"], ["business", "Business"]]], ["seller_name", "Full name / company name"],
    ["seller_address", "Address (one line per row)", "textarea"], ["seller_postcode", "Postcode"], ["seller_phone", "Telephone"], ["seller_email", "Email"]]],
  ["Vehicle", [["registration", "Registration"], ["vin", "VIN"], ["make", "Make"], ["model", "Model"], ["derivative", "Derivative / trim"], ["colour", "Colour"],
    ["engine_no", "Engine no."], ["mileage", "Mileage at inspection", "number"], ["first_registration", "Date of first registration", "date"], ["mot_expiry", "MOT expiry", "date"]]],
  ["Finance", [["outstanding_finance", "Outstanding finance we settle (£)", "number"], ["finance_company", "Finance company and agreement no."]]],
  ["Notes", [["notes", "Internal notes (not printed)", "textarea"]]],
];

const isoOrBlank = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || "") ? s : "");
const fromValuation = (v) => ({
  valuation_id: v.id, seller_name: v.name || "", seller_email: v.email || "", seller_phone: v.phone || "", seller_postcode: v.postcode || "",
  registration: v.registration || "", make: v.make || "", model: v.model || "", derivative: v.derivative || "", colour: v.colour || "",
  mileage: String(v.mileage || "").replace(/\D/g, ""), mot_expiry: isoOrBlank(v.mot_expiry),
});

const toBody = (f) => ({
  ...f, price: Number(f.price), outstanding_finance: Number(f.outstanding_finance) || 0, mileage: f.mileage === "" || f.mileage == null ? null : Number(f.mileage),
});

export function PurchaseForm({ initial, agreementNo, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [valuations, setValuations] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    api.get("/admin/valuations").then((r) => setValuations(Array.isArray(r.data) ? r.data : [])).catch(() => setValuations([]));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.seller_name.trim() || !f.model.trim() || !(Number(f.price) > 0)) return toast.error("Seller name, model and a purchase price are required");
    setBusy(true);
    try {
      const { data } = agreementNo ? await api.put(`/admin/purchases/${agreementNo}`, toBody(f)) : await api.post("/admin/purchases", toBody(f));
      toast.success(`Purchase ${data.agreement_no} saved`);
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border border-stone bg-paper p-6" data-testid="purchase-form">
      <h2 className="mb-5 font-serif text-2xl">{agreementNo ? `Edit purchase ${agreementNo}` : "Record a purchase"}</h2>
      <label htmlFor="pur-valuation-pick" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Fill from a valuation request (optional)</label>
      <select id="pur-valuation-pick" className="field mb-6 !py-2.5 text-sm" data-testid="purchase-valuation-pick" value={f.valuation_id}
        onChange={(e) => { const v = valuations.find((x) => x.id === e.target.value); setF((p) => (v ? { ...p, ...fromValuation(v) } : { ...p, valuation_id: "" })); }}>
        <option value="">None / enter manually</option>
        {valuations.map((v) => <option key={v.id} value={v.id}>{[v.registration, v.make, v.model, "·", v.name].filter(Boolean).join(" ")}</option>)}
      </select>
      {SECTIONS.map(([title, fields]) => (
        <fieldset key={title} className="mb-6">
          <legend className="mb-3 text-sm font-semibold text-forest">{title}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map(([k, l, t, o]) => <Field key={k} k={k} label={l} type={t} options={o} value={f[k]} onChange={set(k)} prefix="purchase" />)}
          </div>
        </fieldset>
      ))}
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={busy} className="btn btn-primary !py-3" data-testid="purchase-save">{busy ? "Saving…" : agreementNo ? "Save changes" : "Save purchase"}</button>
        <button type="button" onClick={onCancel} className="btn !py-3" data-testid="purchase-cancel">Cancel</button>
      </div>
    </form>
  );
}
